import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiServices from '../../Services/ApiServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faVideo,
  faUpload,
  faCheck,
  faArrowRight,
  faFilm,
  faCloudUploadAlt,
  faQuestionCircle,
  faPlusCircle,
  faListCheck,
  faPaperclip,
  faFolderOpen,
  faSearch,
  faTimes,
  faPlay,
  faEdit,
  faLightbulb,
  faSync,
  faGripVertical,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { toastHelper } from '../../../Helpers/toastHelper';
import WarningAlert from "../../Shared/WarningAlert";
import DynamicAlert from "../../Shared/DynamicAlert";
import { getFullFileUrl } from '../../../Helpers/UrlHelpers';
import Loader from '../../Shared/Loader';
import MiniLoader from '../../Shared/MiniLoader';
import ProgressBarLoader from '../../Shared/ProgressBarLoader';
import { motion, AnimatePresence } from 'framer-motion';
import extractErrorMessage from '../../../Helpers/APIHelper';
import VideoViewerV2 from '../../Shared/VideoViewerV2';
import FileUploader from '../../Shared/FileUploader';
import { useLanguage } from '../../../contexts/LanguageContext';
import CourseVideoQuizes from './CourseVideoQuizes';
import CourseVideoAttachments from './CourseVideoAttachments';
import DynamicDrawer from '../../Shared/DynamicDrawer';
import ComponentPopup from '../../Shared/ComponentPopup';
import DeleteAlert from '../../Shared/DeleteAlert';
import DynamicModal from '../../Shared/DynamicModal';
import * as Yup from 'yup';
import StyledText from '../../Shared/StyledText';
import htmlHelper from '../../../Helpers/htmlHelper';


const VideoCardItem = React.memo(({
  video,
  index,
  draggedIndex,
  isSearchActive,
  courseName,
  t,
  formatVideoSize,
  deletingVideoId,
  onAddQuestion,
  onAddAttachment,
  onViewAttachments,
  onViewQuizzes,
  onEditDescription,
  onUpdateVideo,
  onDeleteVideo,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoUrl = useMemo(() => getFullFileUrl(video.video), [video.video]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.25 }}
      className={`cvm-video-card glass-panel ${draggedIndex === index ? "dragging" : ""}`}
      draggable={!isSearchActive}
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={onDragEnd}
    >
      <div className="video-player-wrapper">
        {isPlaying ? (
          <VideoViewerV2
            url={videoUrl}
            title={htmlHelper.stripHtml(video.description)}
            description={`${t('cvm_videoFromCourse')} ${courseName || ''}`}
            className="cvm-player"
            autoPlay={true}
            muted={false}
            preload="auto"
          />
        ) : (
          <div className="cvm-poster-preview" onClick={() => setIsPlaying(true)} title={htmlHelper.stripHtml(video.description)}>
            <video
              src={`${videoUrl}#t=0.5`}
              preload="metadata"
              className="cvm-player-thumb"
            />
            <div className="cvm-play-overlay">
              <div className="cvm-play-btn">
                <FontAwesomeIcon icon={faPlay} />
              </div>
            </div>
            <div className="video-overlay-gradient"></div>
          </div>
        )}
      </div>
      <div className="video-info-box">
        <div className="video-title" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
          <div title={htmlHelper.stripHtml(video.description)} dangerouslySetInnerHTML={{ __html: video.description }}></div>
          {!isSearchActive && (
            <div className="drag-handle" style={{ cursor: 'grab', color: '#94a3b8', padding: '4px' }} title={t('cvm_dragToReorder') || 'اسحب لترتيب الفيديو'}>
              <FontAwesomeIcon icon={faGripVertical} />
            </div>
          )}
        </div>
        <div className="video-meta">
          <span className="video-date">{video.created_at || t('cvm_addedRecently')}</span>
          <span className="video-size">{t('cvm_size')}{formatVideoSize(video.size)}</span>
        </div>

        {/* Dedicated Actions Row */}
        <div className="video-actions-row">
          <button
            className="cvm-action-btn cvm-action-add-q"
            onClick={() => onAddQuestion(video)}
            title={t('cvm_addNewQuestionBtn')}
          >
            <FontAwesomeIcon icon={faPlusCircle} />
          </button>
          <button
            className="cvm-action-btn cvm-action-attachment"
            onClick={() => onAddAttachment(video)}
            title={t('cva_addAttachmentBtn') || 'إضافة مرفق للفيديو'}
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </button>
          <button
            className="cvm-action-btn cvm-action-view-attachments"
            onClick={() => onViewAttachments(video)}
            title={t('cva_viewAttachmentsBtn') || 'عرض وإدارة مرفقات الفيديو'}
          >
            <FontAwesomeIcon icon={faFolderOpen} />
          </button>
          <button
            className="cvm-action-btn cvm-action-quiz-list"
            onClick={() => onViewQuizzes(video)}
            title={t('cvm_viewManageQuizzes')}
          >
            <FontAwesomeIcon icon={faListCheck} />
          </button>
          <button
            className="cvm-action-btn cvm-action-edit"
            onClick={() => onEditDescription(video)}
            title={t('cvm_editVideoDescription') || 'تعديل وصف الفيديو'}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className="cvm-action-btn cvm-action-update-video"
            onClick={() => onUpdateVideo(video)}
            title={t('cvm_updateVideoBtn') || 'تحديث ملف الفيديو'}
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button
            className="cvm-action-btn cvm-action-delete"
            onClick={() => onDeleteVideo(video)}
            disabled={deletingVideoId === video.id}
            title={t('cvm_deleteVideo')}
          >
            {deletingVideoId === video.id ? (
              <MiniLoader size={16} color="#EF4444" variant="dots" />
            ) : (
              <FontAwesomeIcon icon={faTrash} />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
});

const CourseVideoManager = () => {
  const { t } = useLanguage();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([{ description: '', video: null, dragOver: false }]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [deletingVideoId, setDeletingVideoId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('existing');
  const [course, setCourse] = useState(null);
  
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  const isSearchActive = searchQuery.trim().length > 0;

  const filteredExistingVideos = useMemo(() => {
    if (!isSearchActive) return existingVideos;
    const query = searchQuery.toLowerCase().trim();
    return existingVideos.filter((video) =>
      (htmlHelper.stripHtml(video.description) || '').toLowerCase().includes(query)
    );
  }, [existingVideos, searchQuery, isSearchActive]);

  // Quiz Modal State
  const [selectedQuizVideo, setSelectedQuizVideo] = useState(null);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);

  // View Attachments Modal State
  const [selectedAttachmentsVideo, setSelectedAttachmentsVideo] = useState(null);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);

  // Delete Video Alert State
  const [deleteVideoAlertOpen, setDeleteVideoAlertOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleteVideoLoading, setDeleteVideoLoading] = useState(false);

  // Edit Video Description Modal State
  const [selectedEditVideo, setSelectedEditVideo] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Add Question Drawer State
  const [selectedAddQuestionVideo, setSelectedAddQuestionVideo] = useState(null);
  const [isAddQuestionDrawerOpen, setIsAddQuestionDrawerOpen] = useState(false);

  // Add Attachment Drawer State
  const [selectedAttachmentVideo, setSelectedAttachmentVideo] = useState(null);
  const [isAttachmentDrawerOpen, setIsAttachmentDrawerOpen] = useState(false);

  // Update Video Drawer State
  const [selectedUpdateVideo, setSelectedUpdateVideo] = useState(null);
  const [isUpdateVideoDrawerOpen, setIsUpdateVideoDrawerOpen] = useState(false);
  const [updateVideoProgress, setUpdateVideoProgress] = useState(0);
  const [updatingVideo, setUpdatingVideo] = useState(false);

  const handleOpenEditDescription = useCallback((video) => {
    setSelectedEditVideo(video);
    setIsEditModalOpen(true);
  }, []);

  const handleOpenAddQuestion = useCallback((video) => {
    setSelectedAddQuestionVideo(video);
    setIsAddQuestionDrawerOpen(true);
  }, []);

  const handleOpenAddAttachment = useCallback((video) => {
    setSelectedAttachmentVideo(video);
    setIsAttachmentDrawerOpen(true);
  }, []);

  const handleOpenUpdateVideo = useCallback((video) => {
    setSelectedUpdateVideo(video);
    setIsUpdateVideoDrawerOpen(true);
  }, []);

  const handleOpenViewAttachments = useCallback((video) => {
    setSelectedAttachmentsVideo(video);
    setIsAttachmentsModalOpen(true);
  }, []);

  const handleOpenViewQuizzes = useCallback((video) => {
    setSelectedQuizVideo(video);
    setIsQuizModalOpen(true);
  }, []);

  const editFormConfig = useMemo(() => [
    {
      name: 'description',
      label: t('cvm_videoTitleDescription') || 'وصف الفيديو',
      type: 'styledText',
      required: true,
      defaultValue: selectedEditVideo?.description || '',
      icon: faEdit,
    }
  ], [selectedEditVideo, t]);

  const handleUpdateDescriptionSubmit = async (formData) => {
    if (!selectedEditVideo) return;
    try {
      await ApiServices.put(
        `CourseVideo/updateDescription/${selectedEditVideo.id}`,
        { description: formData.description }
      );
      toastHelper.success(t('cvm_updateDescriptionSuccess') || 'تم تحديث وصف الفيديو بنجاح');
      fetchCourseVideos();
      setIsEditModalOpen(false);
      setSelectedEditVideo(null);
    } catch (error) {
      toastHelper.error(error?.msg || error?.message || t('cvm_updateDescriptionError') || 'تعذر تحديث وصف الفيديو');
      throw error;
    }
  };

  const addQuestionFormConfig = useMemo(() => {
    const config = [
      {
        name: 'question',
        label: t('examQuestionText'),
        type: 'text',
        required: true,
      },
      {
        name: 'explanation',
        label: t('cvm_explanationLabel'),
        type: 'text',
        required: false,
      },
      {
        name: 'timestamp_time',
        label: t('cvm_timestampLabelMinutes') || 'وقت ظهور السؤال',
        type: 'time',
        required: true,
        defaultValue: '00:00:00',
        useSimpleTime: true,
      },
      {
        name: 'score',
        label: t('cvm_scoreLabel'),
        type: 'number',
        required: true,
        min: 0,
        defaultValue: 1,
      },
    ];

    for (let i = 0; i < 4; i++) {
      config.push({
        name: `choice${i + 1}_text`,
        label: `${t('cvm_choiceLabel')} ${i + 1}`,
        type: 'text',
        required: i < 2,
        defaultValue: '',
        validation: Yup.string()
          .nullable()
          .test('unique', t('examQuestionValidationDuplicate') || t('cvm_uniqueChoiceValidation'), function (value) {
            if (!value || value.trim() === '') return true;
            const { choice1_text, choice2_text, choice3_text, choice4_text } = this.parent;
            const allValues = [choice1_text, choice2_text, choice3_text, choice4_text];
            const count = allValues.filter((v) => v && v.trim() === value.trim()).length;
            return count <= 1;
          })
      });
      if (i < 2) {
        config[config.length - 1].validation = config[config.length - 1].validation.required(t('requiredField') || 'هذا الحقل مطلوب');
      }
      config.push({
        name: `choice${i + 1}_correct`,
        label: (t('cvm_isChoiceCorrect') || 'هل الخيار {n} صحيح؟').replace('{n}', i + 1),
        type: 'select',
        required: i < 2,
        options: [
          { label: t('cvm_correctOption'), value: 'true' },
          { label: t('cvm_incorrectOption'), value: 'false' },
        ],
        defaultValue: i === 0 ? 'true' : 'false',
      });
    }

    return config;
  }, [t]);

  const handleAddQuestionSubmit = async (formData) => {
    if (!selectedAddQuestionVideo) return;

    const choices = [];
    for (let i = 1; i <= 4; i++) {
      const text = formData[`choice${i}_text`];
      const isCorrect = formData[`choice${i}_correct`] === 'true';
      if (text && text.trim() !== '') {
        choices.push({
          text: text.trim(),
          is_correct: isCorrect,
        });
      }
    }

    const uniqueTexts = new Set(choices.map(c => c.text));
    if (uniqueTexts.size !== choices.length) {
      toastHelper.error(t('cvq_duplicateOptionError'));
      throw new Error('Duplicate options are not allowed');
    }

    // Parse HH:MM:SS time string to total seconds
    const timeValue = formData.timestamp_time || '00:00:00';
    const [hours, minutes, seconds] = timeValue.split(':').map(Number);
    const totalSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);

    const payload = {
      course_video_id: Number(selectedAddQuestionVideo.id),
      timestamp_in_seconds: totalSeconds,
      question: formData.question,
      explanation: formData.explanation || '',
      score: Number(formData.score),
      choices: choices,
    };

    try {
      await ApiServices.post(`CourseVideoQuestion/create`, payload);
      toastHelper.success(t('cvq_addQuestionSuccess'));
      setIsAddQuestionDrawerOpen(false);
      setSelectedAddQuestionVideo(null);
    } catch (error) {
      toastHelper.error(error?.msg || t('addEditMozakaraSaveFailed'));
      throw error;
    }
  };

  const attachmentFormConfig = useMemo(() => [
    {
      name: 'Title',
      label: t('cva_attachmentTitleLabel') || 'عنوان المرفق',
      type: 'text',
      required: true,
    },
    {
      name: 'Description',
      label: t('cva_attachmentDescLabel') || 'وصف المرفق (اختياري)',
      type: 'styledText',
      required: false,
    },
    {
      name: 'Attachment',
      label: t('cva_attachmentFileLabel') || 'ملف المرفق',
      type: 'file',
      accept: '*/*',
      required: true,
      subText: t('cva_fileSubText') || 'اختر ملف المرفق (PDF, DOCX, صور, إلخ)',
    }
  ], [t]);

  const handleAttachmentSubmit = async (formData) => {
    if (!selectedAttachmentVideo) return;

    const fd = new FormData();
    fd.append('CourseVideoId', selectedAttachmentVideo.id);
    fd.append('Title', formData.Title);
    fd.append('Description', formData.Description || '');
    if (formData.Attachment) {
      fd.append('Attachment', formData.Attachment);
    }

    try {
      await ApiServices.post('CourseVideoAttachment/create', fd, true);
      toastHelper.success(t('cva_addAttachmentSuccess') || 'تم إضافة المرفق بنجاح');
      setIsAttachmentDrawerOpen(false);
      setSelectedAttachmentVideo(null);
    } catch (error) {
      toastHelper.error(error?.msg || t('cva_addAttachmentError') || 'تعذر إضافة المرفق');
      throw error;
    }
  };
  const updateVideoFormConfig = useMemo(() => [
    {
      name: 'Video',
      label: t('cvm_updateVideoFileLabel') || 'ملف الفيديو الجديد',
      type: 'file',
      accept: 'video/mp4,video/x-m4v,video/*',
      required: true,
      MaxFileSize: 500,
      subText: t('cvm_updateVideoFileSubText') || 'اختر ملف الفيديو الجديد لاستبدال الحالي (MP4, WebM - حد أقصى 500MB)',
    }
  ], [t]);

  const handleUpdateVideoSubmit = async (formData) => {
    if (!selectedUpdateVideo) return;

    const fd = new FormData();
    fd.append('CourseVideoId', selectedUpdateVideo.id);
    if (formData.Video) {
      fd.append('Video', formData.Video);
    }

    setUpdatingVideo(true);
    setUpdateVideoProgress(0);

    try {
      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUpdateVideoProgress(percentCompleted);
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      };

      await ApiServices.put('CourseVideo/updateVideo', fd, true, 6000000, config);
      toastHelper.success(t('cvm_updateVideoSuccess') || 'تم تحديث ملف الفيديو بنجاح');
      fetchCourseVideos();
      setIsUpdateVideoDrawerOpen(false);
      setSelectedUpdateVideo(null);
    } catch (error) {
      toastHelper.error(error?.msg || error?.message || t('cvm_updateVideoError') || 'تعذر تحديث ملف الفيديو');
      throw error;
    } finally {
      setUpdatingVideo(false);
      setUpdateVideoProgress(0);
    }
  };

  const [courseDetails, setCourseDetails] = useState(null);

  const [dynamicAlertState, setDynamicAlertState] = useState({
    isOpen: false,
    resolve: null,
    title: "",
    message: "",
    confirmText: "",
    cancelText: "",
    icon: "warning",
    input: null,
    inputLabel: "",
    inputPlaceholder: "",
    inputValidator: null
  });

  const showDynamicAlert = (options) => {
    return new Promise((resolve) => {
      setDynamicAlertState({
        isOpen: true,
        resolve,
        title: options.title,
        message: options.text,
        confirmText: options.confirmButtonText || t('confirm'),
        cancelText: options.cancelButtonText || t('cancel'),
        icon: options.icon || "warning",
        input: options.input,
        inputLabel: options.inputLabel,
        inputPlaceholder: options.inputPlaceholder,
        inputValidator: options.inputValidator
      });
    });
  };

  const handleDynamicAlertConfirm = (inputValue) => {
    if (dynamicAlertState.inputValidator) {
      const validationError = dynamicAlertState.inputValidator(inputValue);
      if (validationError) {
        toastHelper.error(validationError);
        return;
      }
    }
    if (dynamicAlertState.resolve) {
      if (dynamicAlertState.input) {
        dynamicAlertState.resolve({ isConfirmed: true, value: inputValue });
      } else {
        dynamicAlertState.resolve({ isConfirmed: true });
      }
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDynamicAlertCancel = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: false, value: undefined });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    if (courseId) {
      fetchCourseVideos();
    }
  }, [courseId]);

  const fetchCourseVideos = async () => {
    setFetching(true);
    try {
      const [videosRes, courseRes] = await Promise.all([
        ApiServices.get(`CourseVideo/getAll/${courseId}`),
        ApiServices.get(`Course/${courseId}`)
      ]);
      setExistingVideos(videosRes.data || []);
      if(courseRes && courseRes.data) {
        setCourse(courseRes.data);
      }
    } catch (error) {
        setCourse(null);
      toastHelper.error(t('cvm_videosLoadError'));
    }
    setFetching(false);
  };

  const handleDragStart = (e, index) => {
    if (isSearchActive) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (isSearchActive || draggedIndex === null || draggedIndex === dropIndex) return;

    const newVideos = [...existingVideos];
    const draggedVideo = newVideos[draggedIndex];

    newVideos.splice(draggedIndex, 1);
    newVideos.splice(dropIndex, 0, draggedVideo);

    setExistingVideos(newVideos);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSaveOrder = async () => {
    if (isSearchActive || existingVideos.length === 0) return;
    setOrderLoading(true);
    try {
      const orderedIds = existingVideos.map((v) => v.id);
      await ApiServices.put('CourseVideo/updateOrder', orderedIds);
      toastHelper.success(t('cvm_orderSavedSuccess') || 'تم حفظ الترتيب بنجاح');
      fetchCourseVideos();
    } catch (error) {
      toastHelper.error(error.msg || t('cvm_orderSaveFailed') || 'فشل حفظ الترتيب');
    } finally {
      setOrderLoading(false);
    }
  };

  const handleAddVideo = () => {
    if (videos.length >= 3) {
      toastHelper.warning(t('cvm_uploadMaxThreeRecommended') || 'يُنصح برفع 3 فيديوهات كحد أقصى في المرة الواحدة لضمان أداء واستقرار الخادم');
    }
    setVideos([...videos, { description: '', video: null, dragOver: false }]);
  };

  const handleRemoveVideo = (index) => {
    if (videos.length === 1) {
      toastHelper.error(t('cvm_atLeastOneVideoRequired'));
      return;
    }
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handleVideoChange = (index, field, value) => {
    const newVideos = [...videos];
    newVideos[index][field] = value;
    setVideos(newVideos);
  };

  const formatVideoSize = (sizeInMb) => {
    if (sizeInMb === null || sizeInMb === undefined || Number.isNaN(Number(sizeInMb))) {
      return t('cvm_notSpecified');
    }

    const numericSize = Number(sizeInMb);
    if (numericSize >= 1024) {
      return `${(numericSize / 1024).toFixed(2)} GB`;
    }

    return `${numericSize.toFixed(2)} MB`;
  };

  const totalVideoSizeInMb = existingVideos.reduce((total, video) => {
    const numericSize = Number(video?.size);
    if (Number.isNaN(numericSize)) {
      return total;
    }

    return total + numericSize;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('CourseId', courseId);

      videos.forEach((video, index) => {
        formData.append(`CourseVideos[${index}].description`, video.description);
        if (video.video) {
          formData.append(`CourseVideos[${index}].video`, video.video);
        }
      });

      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      };

      await ApiServices.post('CourseVideo/create', formData, true, 6000000, config);
      toastHelper.success(t('cvm_videosAddedSuccess'));
      fetchCourseVideos();
      setVideos([{ description: '', video: null, dragOver: false }]);
      setActiveTab('existing');
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(error?.msg || t('cvm_videosAddError'));
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteVideo = (video) => {
    setVideoToDelete(video);
    setDeleteVideoAlertOpen(true);
  };

  const confirmDeleteVideo = async () => {
    if (!videoToDelete) return;
    const targetId = typeof videoToDelete === 'object' ? videoToDelete?.id : videoToDelete;
    if (!targetId) return;

    setDeleteVideoLoading(true);
    setDeletingVideoId(targetId);
    try {
      await ApiServices.delete(`CourseVideo/delete/${targetId}`);
      toastHelper.success(t('cvm_videoDeletedSuccess') || 'تم حذف الفيديو بنجاح');
      fetchCourseVideos();
    } catch (error) {
      toastHelper.error(error.msg || t('cvm_videoDeleteError') || 'تعذر حذف الفيديو');
    } finally {
      setDeleteVideoLoading(false);
      setDeletingVideoId(null);
      setDeleteVideoAlertOpen(false);
      setVideoToDelete(null);
    }
  };

  if (fetching && !course) {
    return <Loader text={t('loadingData')} />;
  }

  return (
    <div className="cvm-wrapper">

      <div className="cvm-container">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cvm-header glass-panel"
        >
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate("/teacher/dashboard?tab=Courses")}>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          <div className="header-right">
            <div className="title-section">
              <div className="icon-wrapper">
                <FontAwesomeIcon icon={faFilm} />
              </div>
              <div className="text-wrapper">
                <h1>{t('cvm_manageVideos')}</h1>
                {course && <p className="subtitle">{course.name}</p>}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Loading Overlay */}
        <AnimatePresence>
          {(loading || fetching || deletingVideoId) && (
            <motion.div
              className="cvm-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="overlay-content glass-panel">
                {loading && uploadProgress > 0 ? (
                  <ProgressBarLoader progress={uploadProgress} text={t('cvm_uploadingProgress')} />
                ) : (
                  <Loader text={loading ? t('saving') : fetching ? t('loading') : t('deleting')} />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Custom Tabs */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="cvm-tabs-container glass-panel"
        >
          <button
            className={`cvm-tab ${activeTab === 'existing' ? 'active' : ''}`}
            onClick={() => setActiveTab('existing')}
          >
            <FontAwesomeIcon icon={faVideo} className="tab-icon" />
            <span>{t('cvm_videosLibrary')} ({existingVideos.length})</span>
            {activeTab === 'existing' && <motion.div layoutId="activeTabBadge" className="active-indicator" />}
          </button>
          <button
            className={`cvm-tab ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => setActiveTab('add')}
            disabled={!course}
            style={{ opacity: !course ? 0.5 : 1, cursor: !course ? 'not-allowed' : 'pointer' }}
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} className="tab-icon" />
            <span>{t('cvm_uploadNewVideos')}</span>
            {activeTab === 'add' && <motion.div layoutId="activeTabBadge" className="active-indicator" />}
          </button>
        </motion.div>

        {/* Dynamic Content Area */}
        <div className="cvm-content-area">
          <AnimatePresence mode="wait">
            {activeTab === 'existing' ? (
              <motion.div
                key="existing"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.3 }}
                className="existing-videos-section"
              >
                {existingVideos.length > 0 && (
                  <div className="cvm-summary-highlight glass-panel">
                    <div className="summary-icon">
                      <FontAwesomeIcon icon={faFilm} />
                    </div>
                    <div className="summary-text">
                      <span className="summary-label">{t('cvm_totalCourseVideosSize')}</span>
                      <strong className="summary-value">{formatVideoSize(totalVideoSizeInMb)}</strong>
                    </div>
                    <div className="summary-count">
                      <span>{existingVideos.length}</span>
                      <small>{t('cvm_video')}</small>
                    </div>
                  </div>
                )}

                {existingVideos.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="cvm-upload-hint-banner glass-panel"
                    style={{ marginBottom: '1.5rem', marginTop: '1rem' }}
                  >
                    <div className="hint-icon">
                      <FontAwesomeIcon icon={faLightbulb} />
                    </div>
                    <div className="hint-text">
                      <strong>{t('cvm_reorderHintTitle')}</strong>
                      <span> {t('cvm_reorderHintDesc')}</span>
                    </div>
                  </motion.div>
                )}

                {existingVideos.length > 0 && (
                  <div className="cvm-toolbar glass-panel" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div className="cvm-search-wrapper" style={{ flex: 1 }}>
                      <FontAwesomeIcon icon={faSearch} className="search-icon" />
                      <input
                        type="text"
                        className="cvm-search-input"
                        placeholder={t('cvm_searchPlaceholder') || 'ابحث باسم أو عنوان الفيديو...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="search-clear-btn"
                          onClick={() => setSearchQuery('')}
                          title={t('clear') || 'مسح'}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                    {!isSearchActive && existingVideos.length > 1 && (
                      <motion.button
                        className="cvm-btn-primary"
                        onClick={handleSaveOrder}
                        disabled={orderLoading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '150px', justifyContent: 'center' }}
                      >
                        {orderLoading ? (
                          <MiniLoader size={16} color="#fff" variant="dots" />
                        ) : (
                          <FontAwesomeIcon icon={faSave} />
                        )}
                        <span>{t('cvm_saveOrder') || 'حفظ الترتيب'}</span>
                      </motion.button>
                    )}
                  </div>
                )}

                {existingVideos.length === 0 ? (
                  <div className="cvm-empty-state glass-panel">
                    <div className="empty-icon-wrapper">
                      <FontAwesomeIcon icon={faVideo} />
                    </div>
                    <h3>{t('cvm_noVideosYet')}</h3>
                    <p>{t('cvm_enrichCourseContent')}</p>
                    <button 
                      className="cvm-btn-primary mt-4" 
                      onClick={() => setActiveTab('add')}
                      disabled={!course}
                      style={{ opacity: !course ? 0.5 : 1, cursor: !course ? 'not-allowed' : 'pointer' }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> {t('cvm_addNewVideo')}
                    </button>
                  </div>
                ) : filteredExistingVideos.length === 0 ? (
                  <div className="cvm-empty-state glass-panel">
                    <div className="empty-icon-wrapper">
                      <FontAwesomeIcon icon={faSearch} />
                    </div>
                    <h3>{t('cvm_noSearchResults') || 'لا توجد فيديوهات تطابق البحث'}</h3>
                    <button
                      className="cvm-btn-primary mt-4"
                      onClick={() => setSearchQuery('')}
                    >
                      <FontAwesomeIcon icon={faTimes} /> {t('clearSearch') || 'إلغاء البحث'}
                    </button>
                  </div>
                ) : (
                  <div className="cvm-videos-grid">
                    {filteredExistingVideos.map((video, index) => (
                      <VideoCardItem
                        key={video.id}
                        video={video}
                        index={index}
                        draggedIndex={draggedIndex}
                        isSearchActive={isSearchActive}
                        courseName={course?.name}
                        t={t}
                        formatVideoSize={formatVideoSize}
                        deletingVideoId={deletingVideoId}
                        onAddQuestion={handleOpenAddQuestion}
                        onAddAttachment={handleOpenAddAttachment}
                        onViewAttachments={handleOpenViewAttachments}
                        onViewQuizzes={handleOpenViewQuizzes}
                        onEditDescription={handleOpenEditDescription}
                        onUpdateVideo={handleOpenUpdateVideo}
                        onDeleteVideo={handleDeleteVideo}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragEnd={handleDragEnd}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="add"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className="add-videos-section"
              >
                {/* Upload Hint Banner */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="cvm-upload-hint-banner glass-panel"
                >
                  <div className="hint-icon">
                    <FontAwesomeIcon icon={faLightbulb} />
                  </div>
                  <div className="hint-text">
                    <strong>{t('cvm_uploadHintTitle') || 'ملاحظة هامة حول رفع الفيديوهات:'}</strong>
                    <span>
                      {t('cvm_uploadLimitHint') || 'لتجنب الضغط والإجهاد على الخادم بسبب حجم البيانات، يُنصح بعدم رفع عدد كبير من الفيديوهات دفعة واحدة (يفضل 3 فيديوهات كحد أقصى في المرة الواحدة) لضمان اكتمال الرفع بنجاح.'}
                    </span>
                    <div style={{ marginTop: '4px' }}>
                      <strong>{t('cvm_introVideoHintTitle') || 'تنبيه:'}</strong>
                      <span> {t('cvm_introVideoHint') || 'الفيديو الأول في ترتيب القائمة سيُعرض دائماً كفيديو تعريفي (Intro) للدورة للطلاب.'}</span>
                    </div>
                  </div>
                </motion.div>

                <form onSubmit={handleSubmit} className="cvm-form">
                  <div className="form-cards-list">
                    {videos.map((video, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="cvm-form-card glass-panel"
                      >
                        <div className="card-header">
                          <div className="card-badge">{t('cvm_video')} {index + 1}</div>
                          {videos.length > 1 && (
                            <button
                              type="button"
                              className="cvm-btn-icon-danger-soft"
                              onClick={() => handleRemoveVideo(index)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>

                        <div className="input-group">
                          <label>{t('cvm_videoTitleDescription')}</label>
                          <StyledText
                            value={video.description}
                            onChange={(content) => handleVideoChange(index, 'description', content)}
                            placeholder={t('cvm_videoTitlePlaceholder')}
                            className="cvm-input"
                          />
                        </div>

                        <div className="input-group">
                          <label>{t('cvm_videoFile')}</label>
                          <FileUploader
                            id={`video-upload-${index}`}
                            accept="video/mp4,video/x-m4v,video/*"
                            MaxFileSize={500}
                            previewUrl={video.video ? "pdf-placeholder" : null}
                            fileName={video.video ? video.video.name : null}
                            onFileChange={(e) => handleVideoChange(index, 'video', e.target.files[0])}
                            onDragOver={(e) => {
                              e.preventDefault();
                              handleVideoChange(index, 'dragOver', true);
                            }}
                            onDragLeave={() => handleVideoChange(index, 'dragOver', false)}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleVideoChange(index, 'dragOver', false);
                              if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                handleVideoChange(index, 'video', e.dataTransfer.files[0]);
                              }
                            }}
                            imageDragOver={video.dragOver}
                            placeholder={t('cvm_dragOrClickVideo')}
                            subText={t('cvm_maxVideoSize')}
                            buttonText={t('cvm_chooseVideo')}
                            changeButtonText={t('cvm_changeVideo')}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="cvm-form-actions glass-panel">
                    <button
                      type="button"
                      className="cvm-btn-secondary"
                      onClick={handleAddVideo}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      {t('cvm_addAnotherVideo')}
                    </button>

                    <button
                      type="submit"
                      className="cvm-btn-primary-large"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <MiniLoader size={20} color="#fff" />
                          {t('cvm_uploadingProgress')}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCloudUploadAlt} />
                          {t('cvm_startUpload')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <WarningAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "warning"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
        input={dynamicAlertState.input}
        inputLabel={dynamicAlertState.inputLabel}
        inputPlaceholder={dynamicAlertState.inputPlaceholder}
      />

      <DynamicAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "question"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
        confirmColor="#4a90e2"
        input={dynamicAlertState.input}
        inputLabel={dynamicAlertState.inputLabel}
        inputPlaceholder={dynamicAlertState.inputPlaceholder}
      />

      <ComponentPopup
        isOpen={isQuizModalOpen}
        onClose={() => {
          setIsQuizModalOpen(false);
          setSelectedQuizVideo(null);
        }}
        title={t('cvm_videoQuizQuestions') || 'أسئلة الفيديو (الكويزات)'}
        subtitle={
          selectedQuizVideo?.description ? (
            <>
              <FontAwesomeIcon icon={faFilm} style={{ color: '#8b5cf6' }} />
              <div dangerouslySetInnerHTML={{ __html: selectedQuizVideo.description }} style={{ display: 'inline' }}></div>
            </>
          ) : null
        }
        icon={faQuestionCircle}
        Component={CourseVideoQuizes}
        componentProps={{
          courseVideoId: selectedQuizVideo?.id,
          videoTitle: htmlHelper.stripHtml(selectedQuizVideo?.description),
        }}
      />

      <ComponentPopup
        isOpen={isAttachmentsModalOpen}
        onClose={() => {
          setIsAttachmentsModalOpen(false);
          setSelectedAttachmentsVideo(null);
        }}
        title={t('cva_videoAttachmentsTitle') || 'مرفقات الفيديو'}
        subtitle={
          selectedAttachmentsVideo?.description ? (
            <>
              <FontAwesomeIcon icon={faFilm} style={{ color: '#8b5cf6' }} />
              <div dangerouslySetInnerHTML={{ __html: selectedAttachmentsVideo.description }} style={{ display: 'inline' }}></div>
            </>
          ) : null
        }
        icon={faFolderOpen}
        Component={CourseVideoAttachments}
        componentProps={{
          courseVideoId: selectedAttachmentsVideo?.id,
        }}
      />

      <DynamicDrawer
        isOpen={isAddQuestionDrawerOpen}
        onClose={() => {
          setIsAddQuestionDrawerOpen(false);
          setSelectedAddQuestionVideo(null);
        }}
        anchor="right"
        width={540}
        title={selectedAddQuestionVideo ? `${t('cvm_addNewQuestionTitle')}: ${htmlHelper.stripHtml(selectedAddQuestionVideo.description)}` : t('cvm_addNewQuestionTitle')}
        formConfig={addQuestionFormConfig}
        onSubmit={handleAddQuestionSubmit}
        submitButtonText={t('save') || 'حفظ'}
      />

      <DynamicDrawer
        isOpen={isAttachmentDrawerOpen}
        onClose={() => {
          setIsAttachmentDrawerOpen(false);
          setSelectedAttachmentVideo(null);
        }}
        anchor="right"
        width={540}
        title={selectedAttachmentVideo ? `${t('cva_addAttachmentTitle')}: ${htmlHelper.stripHtml(selectedAttachmentVideo.description)}` : t('cva_addAttachmentTitle')}
        formConfig={attachmentFormConfig}
        onSubmit={handleAttachmentSubmit}
        submitButtonText={t('save') || 'حفظ'}
        multipart={true}
      />

      <DynamicDrawer
        isOpen={isUpdateVideoDrawerOpen}
        onClose={() => {
          setIsUpdateVideoDrawerOpen(false);
          setSelectedUpdateVideo(null);
          setUpdateVideoProgress(0);
        }}
        anchor="right"
        width={540}
        title={selectedUpdateVideo ? `${t('cvm_updateVideoTitle') || 'تحديث ملف الفيديو'}: ${htmlHelper.stripHtml(selectedUpdateVideo.description)}` : (t('cvm_updateVideoTitle') || 'تحديث ملف الفيديو')}
        formConfig={updateVideoFormConfig}
        onSubmit={handleUpdateVideoSubmit}
        submitButtonText={updatingVideo ? (t('cvm_updatingVideoProgress') || 'جاري التحديث...') : (t('cvm_updateVideoSubmitBtn') || 'تحديث الفيديو')}
        multipart={true}
      />

      <DeleteAlert
        isOpen={deleteVideoAlertOpen}
        onClose={() => {
          setDeleteVideoAlertOpen(false);
          setVideoToDelete(null);
        }}
        onConfirm={confirmDeleteVideo}
        title={t('cvm_confirmDeleteVideoTitle') || "تأكيد حذف الفيديو"}
        message={t('cvm_confirmDeleteVideoMessage') || "هل أنت متأكد من حذف هذا الفيديو من الدورة؟"}
        itemName={htmlHelper.stripHtml(videoToDelete?.description)}
        loading={deleteVideoLoading}
      />

      <DynamicModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedEditVideo(null);
        }}
        title={t('cvm_editVideoDescriptionTitle') || 'تعديل وصف الفيديو'}
        formConfig={editFormConfig}
        onSubmit={handleUpdateDescriptionSubmit}
        submitButtonText={t('save') || 'حفظ'}
      />

      <style jsx>{`
        .cvm-wrapper {
          position: relative;
          min-height: 100vh;
          background-color: transparent;
          overflow: hidden;
          font-family: 'Tajawal', sans-serif;
          padding: 2rem;
        }

        /* Glassmorphism panel base */
        .glass-panel {
          background: white;
          border: 1px solid rgba(255, 255, 255, 1);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          border-radius: 32px;
        }

        .cvm-container {
          position: relative;
          z-index: 10;
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        /* Header */
        .cvm-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2.5rem;
          border-radius: 24px;
        }

        .header-right .title-section {
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }

        .icon-wrapper {
          width: 56px;
          height: 56px;
          background: #EEF2FF;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6366f1;
          font-size: 1.5rem;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
        }

        .text-wrapper h1 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 800;
          color: #1e293b;
          letter-spacing: -0.5px;
        }

        .text-wrapper .subtitle {
          margin: 0.25rem 0 0 0;
          font-size: 1rem;
          color: #64748b;
          font-weight: 500;
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          color: #475569;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .back-btn:hover {
          background: #f8fafc;
          color: #1e293b;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          transform: translateY(-2px);
        }

        /* Tabs */
        .cvm-tabs-container {
          display: flex;
          gap: 1rem;
          padding: 0.75rem;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.6);
        }

        .cvm-tab {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex: 1;
          padding: 1rem;
          border: none;
          background: transparent;
          color: #64748b;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          border-radius: 14px;
          transition: color 0.3s ease;
          font-family: inherit;
          z-index: 1;
        }

        .cvm-tab:hover {
          color: #334155;
        }

        .cvm-tab.active {
          color: #4f46e5;
        }

        .tab-icon {
          font-size: 1.25rem;
        }

        .active-indicator {
          position: absolute;
          inset: 0;
          background: white;
          border-radius: 14px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          z-index: -1;
        }

        /* Overlay */
        .cvm-overlay {
          position: absolute;
          inset: 0;
          border-radius: 24px;
          background: rgba(248, 250, 252, 0.7);
          backdrop-filter: blur(8px);
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .overlay-content {
          padding: 2.5rem 4rem;
          border-radius: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
        }

        /* Empty State */
        .cvm-empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 6rem 2rem;
          border-radius: 30px;
          text-align: center;
        }

        .empty-icon-wrapper {
          width: 96px;
          height: 96px;
          background: #f1f5f9;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: #94a3b8;
          margin-bottom: 1.5rem;
        }

        .cvm-empty-state h3 {
          font-size: 1.5rem;
          color: #1e293b;
          margin: 0 0 0.5rem 0;
          font-weight: 800;
        }

        .cvm-empty-state p {
          color: #64748b;
          font-size: 1.1rem;
          margin: 0 0 2rem 0;
        }

        /* Video Grid */
        .cvm-videos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 2rem;
        }

        .cvm-summary-highlight {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1.5rem 1.75rem;
          border-radius: 24px;
          margin-bottom: 1.5rem;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
        }

        .summary-icon {
          width: 56px;
          height: 56px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6366f1;
          font-size: 1.35rem;
          background: #EEF2FF;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
        }

        .summary-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .summary-label {
          color: #475569;
          font-size: 0.95rem;
          font-weight: 700;
        }

        .summary-value {
          color: #1e293b;
          font-size: 1.4rem;
          font-weight: 800;
        }

        .summary-count {
          min-width: 88px;
          text-align: center;
          padding: 0.9rem 1rem;
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.75);
          color: #334155;
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .summary-count span {
          font-size: 1.25rem;
          font-weight: 800;
          color: #4f46e5;
          line-height: 1;
        }

        .summary-count small {
          font-size: 0.82rem;
          font-weight: 700;
          color: #64748b;
        }

        /* Search Toolbar */
        .cvm-toolbar {
          margin-bottom: 1.5rem;
          padding: 0.75rem 1.25rem;
          border-radius: 20px;
        }

        .cvm-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .cvm-search-wrapper .search-icon {
          position: absolute;
          right: 1.25rem;
          color: #94a3b8;
          font-size: 1rem;
          pointer-events: none;
        }

        [dir="ltr"] .cvm-search-wrapper .search-icon {
          right: auto;
          left: 1.25rem;
        }

        .cvm-search-input {
          width: 100%;
          padding: 0.85rem 3rem;
          font-size: 0.95rem;
          font-weight: 600;
          color: #1e293b;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 14px;
          outline: none;
          transition: all 0.25s ease;
          font-family: inherit;
        }

        .cvm-search-input:focus {
          background: #ffffff;
          border-color: #6366f1;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
        }

        .search-clear-btn {
          position: absolute;
          left: 1.25rem;
          background: #e2e8f0;
          border: none;
          color: #64748b;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 0.8rem;
          transition: all 0.2s ease;
        }

        [dir="ltr"] .search-clear-btn {
          left: auto;
          right: 1.25rem;
        }

        .search-clear-btn:hover {
          background: #cbd5e1;
          color: #1e293b;
        }

        .cvm-video-card {
          border-radius: 24px;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          contain: content;
        }

        .cvm-video-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
        }

        .video-player-wrapper {
          position: relative;
          aspect-ratio: 16/9;
          background: #0f172a;
          overflow: hidden;
        }

        .cvm-poster-preview {
          position: relative;
          width: 100%;
          height: 100%;
          background: #0f172a;
          cursor: pointer;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .cvm-player-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.85;
          transition: all 0.3s ease;
        }

        .cvm-poster-preview:hover .cvm-player-thumb {
          opacity: 1;
          transform: scale(1.04);
        }

        .cvm-play-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(15, 23, 42, 0.3);
          backdrop-filter: blur(2px);
          transition: all 0.3s ease;
          z-index: 2;
        }

        .cvm-poster-preview:hover .cvm-play-overlay {
          background: rgba(15, 23, 42, 0.15);
        }

        .cvm-play-btn {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          box-shadow: 0 8px 25px rgba(99, 102, 241, 0.45);
          transition: all 0.3s ease;
          padding-left: 3px;
        }

        [dir="rtl"] .cvm-play-btn {
          padding-left: 0;
          padding-right: 3px;
        }

        .cvm-poster-preview:hover .cvm-play-btn {
          transform: scale(1.12);
          box-shadow: 0 12px 30px rgba(99, 102, 241, 0.65);
        }

        .cvm-player {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .video-overlay-gradient {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(0deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%);
          pointer-events: none;
        }

        .video-info-box {
          padding: 1.5rem;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background: white;
        }

        .video-title {
          margin: 0 0 1rem 0;
          font-size: 1.15rem;
          color: #1e293b;
          font-weight: 700;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .video-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 0.75rem;
          border-top: 1px dashed #e2e8f0;
        }

        .video-title {
          font-size: 1.15rem; font-weight: 900; color: #0f172a; margin: 0 0 8px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .video-title p { margin: 0; display: inline; }

        .video-size {
          font-size: 0.88rem;
          color: #475569;
          font-weight: 700;
        }

        .video-actions-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1rem;
          padding-top: 0.85rem;
          border-top: 1px solid #f1f5f9;
        }

        .cvm-action-btn {
          width: 38px;
          height: 38px;
          border-radius: 12px;
          border: 1px solid transparent;
          font-size: 0.95rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.25s ease;
        }

        .cvm-action-add-q {
          background: #ecfdf5;
          color: #059669;
          border-color: #a7f3d0;
        }
        .cvm-action-add-q:hover {
          background: #10b981;
          color: #fff;
          border-color: #10b981;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
        }

        .cvm-action-attachment {
          background: #e0f2fe;
          color: #0284c7;
          border-color: #bae6fd;
        }
        .cvm-action-attachment:hover {
          background: #0284c7;
          color: #fff;
          border-color: #0284c7;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(2, 132, 199, 0.25);
        }

        .cvm-action-view-attachments {
          background: #fef3c7;
          color: #d97706;
          border-color: #fde68a;
        }
        .cvm-action-view-attachments:hover {
          background: #d97706;
          color: #fff;
          border-color: #d97706;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(217, 119, 6, 0.25);
        }

        .cvm-action-quiz-list {
          background: #e0e7ff;
          color: #4f46e5;
          border-color: #c7d2fe;
        }
        .cvm-action-quiz-list:hover {
          background: #6366f1;
          color: #fff;
          border-color: #6366f1;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
        }

        .cvm-action-edit {
          background: #f3e8ff;
          color: #9333ea;
          border-color: #e9d5ff;
        }
        .cvm-action-edit:hover {
          background: #9333ea;
          color: #fff;
          border-color: #9333ea;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(147, 51, 234, 0.25);
        }

        .cvm-action-update-video {
          background: #ecfdf5;
          color: #059669;
          border-color: #a7f3d0;
        }
        .cvm-action-update-video:hover {
          background: #059669;
          color: #fff;
          border-color: #059669;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.25);
        }

        /* Upload Hint Banner */
        .cvm-upload-hint-banner {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          padding: 1.25rem 1.75rem;
          margin-bottom: 1.75rem;
          background: linear-gradient(135deg, #fffbe6 0%, #fef3c7 100%);
          border: 1.5px solid #fde68a;
          border-radius: 20px;
          color: #92400e;
          box-shadow: 0 4px 15px rgba(217, 119, 6, 0.08);
        }

        .cvm-upload-hint-banner .hint-icon {
          width: 48px;
          height: 48px;
          min-width: 48px;
          border-radius: 14px;
          background: #fef08a;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.35rem;
          color: #d97706;
          box-shadow: 0 2px 8px rgba(217, 119, 6, 0.15);
        }

        .cvm-upload-hint-banner .hint-text {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .cvm-upload-hint-banner .hint-text strong {
          font-size: 1.05rem;
          font-weight: 800;
          color: #78350f;
        }

        .cvm-upload-hint-banner .hint-text span {
          font-size: 0.95rem;
          font-weight: 600;
          color: #92400e;
          line-height: 1.5;
        }

        .cvm-action-delete {
          background: #fef2f2;
          color: #dc2626;
          border-color: #fecaca;
        }
        .cvm-action-delete:hover:not(:disabled) {
          background: #ef4444;
          color: #fff;
          border-color: #ef4444;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
        }
        .cvm-action-delete:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Forms */
        .form-cards-list {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .cvm-form-card {
          padding: 2.5rem;
          border-radius: 24px;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }

        .card-badge {
          background: #eff6ff;
          color: #3b82f6;
          padding: 0.5rem 1.25rem;
          border-radius: 12px;
          font-weight: 800;
          font-size: 1.1rem;
        }

        .input-group {
          margin-bottom: 1.5rem;
        }

        .input-group label {
          display: block;
          margin-bottom: 0.75rem;
          font-weight: 700;
          color: #334155;
          font-size: 1.05rem;
        }

        .cvm-input {
          width: 100%;
          padding: 1.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          font-size: 1.05rem;
          font-family: inherit;
          transition: all 0.3s ease;
          background: white;
        }

        .cvm-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.1);
        }

        .cvm-file-dropzone {
          position: relative;
        }

        .file-input-hidden {
          position: absolute;
          width: 0;
          height: 0;
          opacity: 0;
        }

        .dropzone-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 2rem;
          border: 2px dashed #cbd5e1;
          border-radius: 20px;
          background: #f8fafc;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
        }

        .dropzone-label:hover {
          border-color: #8b5cf6;
          background: #f5f3ff;
        }

        .dropzone-label.has-file {
          border: 2px solid #10b981;
          background: #ecfdf5;
        }

        .up-icon {
          font-size: 2.5rem;
          color: #94a3b8;
          margin-bottom: 1rem;
          transition: color 0.3s ease;
        }

        .dropzone-label:hover .up-icon {
          color: #8b5cf6;
        }

        .dropzone-label.has-file .up-icon {
          color: #10b981;
        }

        .main-text {
          display: block;
          font-size: 1.15rem;
          font-weight: 700;
          color: #334155;
          margin-bottom: 0.5rem;
        }

        .sub-text {
          display: block;
          font-size: 0.95rem;
          color: #64748b;
        }

        .file-name {
          display: block;
          font-size: 1.1rem;
          font-weight: 700;
          color: #065f46;
          margin-bottom: 0.5rem;
        }

        .change-text {
          display: block;
          font-size: 0.95rem;
          color: #059669;
        }

        .cvm-form-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2.5rem;
          border-radius: 24px;
          margin-top: 1rem;
        }

        /* Buttons */
        .cvm-btn-primary {
          background: linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 14px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.3s ease;
          font-family: inherit;
          box-shadow: 0 8px 20px rgba(79, 70, 229, 0.25);
        }

        .cvm-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 25px rgba(79, 70, 229, 0.35);
        }

        .cvm-btn-primary-large {
          background: linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%);
          color: white;
          border: none;
          padding: 1.25rem 3rem;
          border-radius: 16px;
          font-size: 1.15rem;
          font-weight: 800;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.3s ease;
          font-family: inherit;
          box-shadow: 0 10px 25px rgba(79, 70, 229, 0.3);
        }

        .cvm-btn-primary-large:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 15px 30px rgba(79, 70, 229, 0.4);
        }

        .cvm-btn-primary-large:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .cvm-btn-secondary {
          background: white;
          color: #4f46e5;
          border: 2px solid #e0e7ff;
          padding: 1.1rem 2rem;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .cvm-btn-secondary:hover {
          border-color: #4f46e5;
          background: #e0e7ff;
        }

        .cvm-btn-icon-danger {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: #fef2f2;
          color: #ef4444;
          border: 1px solid #fee2e2;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1.1rem;
        }

        .cvm-btn-icon-danger:hover:not(:disabled) {
          background: #ef4444;
          color: white;
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
        }

        .cvm-btn-icon-danger-soft {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: transparent;
          color: #ef4444;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1.2rem;
        }

        .cvm-btn-icon-danger-soft:hover {
          background: #fef2f2;
        }

        /* Sweet Alert Custom */
        :global(.modern-popup) {
          border-radius: 24px !important;
          padding: 2rem !important;
          font-family: 'Tajawal', sans-serif !important;
        }
        :global(.modern-confirm), :global(.modern-cancel) {
          border-radius: 12px !important;
          padding: 0.75rem 2rem !important;
          font-weight: 700 !important;
          font-size: 1.1rem !important;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .cvm-wrapper { padding: 1.5rem; }
          .cvm-videos-grid { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
        }

        @media (max-width: 768px) {
          .cvm-header {
            flex-direction: column;
            gap: 1.5rem;
            align-items: flex-start;
          }
          .back-btn { align-self: flex-end; }
          .cvm-tabs-container { flex-direction: column; }
          .cvm-tab { padding: 1.25rem; }
          .cvm-summary-highlight {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }
          .summary-icon {
            margin: 0 auto;
          }
          .summary-text {
            align-items: center;
          }
          .cvm-form-actions {
            flex-direction: column;
            gap: 1rem;
          }
          .cvm-btn-secondary, .cvm-btn-primary-large {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseVideoManager;